import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { cn } from "@lib/cn";
import { useState } from "react";
import { useAiImageStore } from "../store";

type TPropertyOptions = {
  placement?: "default" | "nested";
  label?: string;
  options: readonly {
    label: string;
    value: string;
    ratio?: string;
  }[];
  type:
    | "types"
    | "layouts"
    | "aspectRatios"
    | "styles"
    | "views"
    | "backgrounds"
    | "variants";
  icon?: string;
  default?: string;
};

export const AiImageSelect = ({
  label,
  options,
  icon,
  type,
  default: defaultValue,
  placement = "default",
}: TPropertyOptions) => {
  const [open, setOpen] = useState(false);

  const selected = useAiImageStore((s) => {
    switch (type) {
      case "types":
        return s.type;
      case "layouts":
        return s.layout;
      case "aspectRatios":
        return s.aspectRatio;
      case "styles":
        return s.style;
      case "views":
        return s.view;
      case "backgrounds":
        return s.background;
      case "variants":
        return s.variant;
      default:
        return "";
    }
  });

  const setType = useAiImageStore((s) => s.setType);
  const setLayout = useAiImageStore((s) => s.setLayout);
  const setAspectRatio = useAiImageStore((s) => s.setAspectRatio);
  const setStyle = useAiImageStore((s) => s.setStyle);
  const setView = useAiImageStore((s) => s.setView);
  const setBackground = useAiImageStore((s) => s.setBackground);
  const setVariant = useAiImageStore((s) => s.setVariant);

  const setSelected = (value: string) => {
    switch (type) {
      case "types":
        setType(value);
        break;
      case "layouts":
        setLayout(value);
        break;
      case "aspectRatios":
        setAspectRatio(value);
        break;
      case "styles":
        setStyle(value);
        break;
      case "views":
        setView(value);
        break;
      case "backgrounds":
        setBackground(value);
        break;
      case "variants":
        setVariant(value);
        break;
    }
  };

  const option = options.find((option) => option.value === selected);
  const selectedOption = option ?? options.find((option) => option.value === defaultValue);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement={placement === "default" ? "bottom-start" : "left"}
    >
      <Popover.Trigger asChild>
        {placement === "default" ? (
          <button
            aria-label={selectedOption?.label}
            type="button"
            className={cn(
              "tw-h-8 tw-flex tw-items-center tw-gap-1.5 tw-pl-[7px] tw-pr-2 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-700 tw-rounded-[100px] hover:tw-bg-blue-50 tw-transition-colors tw-duration-200",
              {
                "tw-bg-blue-50": open,
              },
            )}
          >
            <Icon icon={icon || "ri-image-line"} />
            {selectedOption?.label}
          </button>
        ) : (
          <button className="tw-w-full tw-text-left tw-px-3 tw-h-8 tw-justify-between tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50">
            {label}
            <span className="tw-flex tw-items-center tw-gap-2 tw-text-blue-500">
              <span className="tw-truncate tw-max-w-24">
                {selectedOption
                  ? `${selectedOption.label}${selectedOption.ratio ? ` (${selectedOption.ratio})` : ""}`
                  : "Automatic"}
              </span>
              <Icon icon="ri-arrow-right-s-line" className="tw-text-blue-700" />
            </span>
          </button>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-z-[99999] tw-p-1 tw-min-w-32">
        <div className="tw-p-1 tw-space-y-0.5 tw-rounded-xl tw-bg-white tw-shadow-popover tw-border tw-border-solid tw-border-blue/5">
          {options.map((option) => (
            <button
              aria-label={option.label}
              type="button"
              key={option.value}
              onClick={() => {
                setSelected(option.value);
                setOpen(false);
              }}
              className={cn(
                "tw-w-full tw-text-left tw-px-3 tw-h-8 tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
                {
                  "tw-bg-primary-50": option.value === selected,
                },
              )}
            >
              {option.label}
              {option.ratio && ` (${option.ratio})`}
            </button>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );
};
